import { Component } from 'react';
import Button from '@shared/componets/ui/Button/Button';
import styles from './style.module.scss';

/**
 * Error Boundary REAL de React (a diferencia del viejo basado en window.onerror, que NO
 * atrapa errores de render y dejaba la pantalla en blanco).
 *
 * Atrapa cualquier excepción lanzada durante el render del árbol hijo y muestra un fallback
 * usable (con botón Reintentar / Recargar) en vez de una pantalla en blanco muda. Crítico para
 * una app de campo: si una pantalla rompe, el chofer puede recuperarse sin reinstalar nada.
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log para que quede en la consola / chrome://inspect y se pueda diagnosticar.
        console.error('[ErrorBoundary] Render error capturado:', error, errorInfo?.componentStack);
        if (typeof this.props.onError === 'function') {
            try { this.props.onError(error, errorInfo); } catch { /* no romper el fallback */ }
        }
    }

    handleReintentar = () => {
        this.setState({ hasError: false, error: null });
    };

    handleRecargar = () => {
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const esDev = process.env.NODE_ENV !== 'production';
        return (
            <div className={styles.wrap} role="alert">
                <div className={styles.card}>
                    <i className={`bi bi-exclamation-triangle ${styles.icon}`} />
                    <h2 className={styles.title}>Algo salió mal</h2>
                    <p className={styles.text}>
                        Esta pantalla tuvo un problema. Podés reintentar; si sigue fallando, recargá la app.
                        Tus datos pendientes no se pierden.
                    </p>
                    <div className={styles.actions}>
                        <Button variant="primary" onClick={this.handleReintentar}>Reintentar</Button>
                        <Button variant="bordergray" onClick={this.handleRecargar}>Recargar app</Button>
                    </div>
                    {esDev && this.state.error && (
                        <details className={styles.detalle}>
                            <summary>Detalle técnico (solo dev)</summary>
                            <pre>{this.state.error?.message}{'\n'}{this.state.error?.stack}</pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
